import { useState } from 'react';
import { hrAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Check, X, Loader2, Plane } from 'lucide-react';
import { toast } from 'sonner';
import { PTO_TYPE_LABELS, formatDateShort } from '@/components/hr/hrUtils';

// Admin: approve / deny the time-off requests that touch the visible week.
export const PendingTimeOffBar = ({ requests, onChanged }) => {
  const [busy, setBusy] = useState(null);
  if (!requests?.length) return null;

  const review = async (req, status) => {
    setBusy(req.id + status);
    try {
      await hrAPI.reviewPtoRequest(req.id, { status });
      toast.success(`${req.employee_name}: time off ${status}${status === 'approved' ? ' — shifts on those days hidden' : ''}`);
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not review request');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 p-3" data-testid="schedule-pending-timeoff">
      <p className="text-sm font-semibold text-amber-800 flex items-center gap-2 mb-2"><Plane className="w-4 h-4" /> {requests.length} time-off request{requests.length === 1 ? '' : 's'} waiting for approval</p>
      <div className="flex flex-col gap-2">
        {requests.map((r) => (
          <div key={r.id} className="flex flex-wrap items-center gap-2 rounded-xl bg-white border border-amber-100 px-3 py-2 text-sm" data-testid={`pending-timeoff-${r.id}`}>
            <span className="font-medium text-slate-800">{r.employee_name}</span>
            <span className="text-slate-500">{PTO_TYPE_LABELS[r.type] || r.type} · {formatDateShort(r.start_date)}{r.end_date !== r.start_date ? ` – ${formatDateShort(r.end_date)}` : ''}{r.days ? ` · ${r.days}d` : ''}</span>
            {r.reason && <span className="text-slate-400 italic truncate max-w-[280px]">“{r.reason}”</span>}
            <div className="ml-auto flex gap-1">
              <Button size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white" disabled={!!busy} onClick={() => review(r, 'approved')} data-testid={`approve-timeoff-${r.id}`}>
                {busy === r.id + 'approved' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4 mr-1" />} Approve
              </Button>
              <Button size="sm" variant="outline" className="h-8 text-red-600 border-red-200 hover:bg-red-50" disabled={!!busy} onClick={() => review(r, 'denied')} data-testid={`deny-timeoff-${r.id}`}>
                {busy === r.id + 'denied' ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4 mr-1" />} Deny
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
